import type { LucideIcon } from 'lucide-react'
import type { ServiceId, SessionId } from '@/types'

import {
    Ambulance,
    Baby,
    Bandage,
    BadgeCheck,
    Clock,
    HeartHandshake,
    Hospital,
    Microscope,
    Pill,
    ScanLine,
    Smile,
    Stethoscope,
    Syringe,
    Users
} from 'lucide-react'

const MAPS_QUERY = 'Klinik Pratama Sehat Budi Luhur, Jl. Kerkof No. 32, Leuwigajah, Cimahi Selatan, Kota Cimahi'

export const clinic = {
    name: 'Klinik Pratama Sehat Budi Luhur',
    brand: 'Sehat Budi Luhur',
    category: 'Klinik Pratama',
    accreditation: 'Terakreditasi Paripurna · LPA-PKP, November 2023',
    accreditationShort: 'Terakreditasi Paripurna (LPA-PKP, 2023)',
    license: 'SIO 503.41/002/1140/DPMPTSP/2019',
    foundation: 'Yayasan Pambudhi Luhur 1976',
    address: 'Jl. Kerkof No. 32, Kelurahan Leuwigajah, Kecamatan Cimahi Selatan, Kota Cimahi, Jawa Barat 40532.',
    landmark: 'Seberang Borma Toserba Kerkof Leuwigajah.',
    phone: {
        label: '(022) 6674146',
        href: 'tel:+62226674146'
    },
    whatsapp: {
        label: '0896-6666-0409',
        href: 'https://wa.me/6289666660409'
    },
    email: {
        label: 'klinik_sehat_budi_luhur@yahoo.co.id',
        href: 'mailto:klinik_sehat_budi_luhur@yahoo.co.id'
    },
    instagram: {
        label: '@kliniksehatbudiluhurcimahi',
        href: 'https://www.instagram.com/kliniksehatbudiluhurcimahi/'
    },
    mapsUrl: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(MAPS_QUERY)}`,
    mapsEmbedUrl: `https://www.google.com/maps?q=${encodeURIComponent(MAPS_QUERY)}&output=embed`
}

export const whatsappLink = (message: string): string => {
    return `${clinic.whatsapp.href}?text=${encodeURIComponent(message)}`
}

export const navLinks = [
    { href: '/#layanan', id: 'layanan', label: 'Layanan' },
    { href: '/#jadwal-dokter', id: 'jadwal-dokter', label: 'Jadwal Dokter' },
    { href: '/#ketersediaan', id: 'ketersediaan', label: 'Ketersediaan' },
    { href: '/#lokasi-kontak', id: 'lokasi-kontak', label: 'Lokasi & Kontak' }
]

export type SessionInfo = {
    label: string
    name: string
    registration: string
    practice: string
    /** Registration window in minutes after midnight, WIB */
    opensAt: number
    closesAt: number
}

export const sessionOrder: SessionId[] = ['pagi', 'sore']

export const sessions: Record<SessionId, SessionInfo> = {
    pagi: {
        label: 'Pagi',
        name: 'Sesi Pagi',
        registration: '07.30–11.30',
        practice: '08.00',
        opensAt: 7 * 60 + 30,
        closesAt: 11 * 60 + 30
    },
    sore: {
        label: 'Sore',
        name: 'Sesi Sore',
        registration: '15.30–19.30',
        practice: '16.00',
        opensAt: 15 * 60 + 30,
        closesAt: 19 * 60 + 30
    }
}

export type BookableService = {
    id: ServiceId
    name: string
    short: string
    description: string
    icon: LucideIcon
    image: string
    imageAlt: string
}

export const serviceOrder: ServiceId[] = ['poli-umum', 'poli-gigi', 'kia-kb']

export const services: Record<ServiceId, BookableService> = {
    'poli-umum': {
        id: 'poli-umum',
        name: 'Poli Umum',
        short: 'Konsultasi kesehatan dasar',
        description: 'Pemeriksaan kesehatan dasar, konsultasi dokter umum, dan pengobatan rawat jalan. Tersedia 3 ruang periksa.',
        icon: Stethoscope,
        image: '/images/poli-umum.jpg',
        imageAlt: 'Dokter memeriksa pasien dengan stetoskop di ruang periksa'
    },
    'poli-gigi': {
        id: 'poli-gigi',
        name: 'Poli Gigi',
        short: 'Perawatan kesehatan gigi dan mulut',
        description: 'Pemeriksaan, penambalan, pencabutan, dan perawatan kesehatan gigi dan mulut.',
        icon: Smile,
        image: '/images/poli-gigi.jpg',
        imageAlt: 'Ruang praktik dokter gigi dengan kursi periksa'
    },
    'kia-kb': {
        id: 'kia-kb',
        name: 'KIA/KB',
        short: 'Layanan ibu, anak, dan keluarga',
        description: 'Pemeriksaan kehamilan, Keluarga Berencana, dan imunisasi.',
        icon: HeartHandshake,
        image: '/images/kia-kb.jpg',
        imageAlt: 'Ibu menggendong bayinya dengan penuh kasih'
    }
}

export type OtherService = {
    name: string
    description: string
    icon: LucideIcon
}

export const otherServices: OtherService[] = [
    {
        name: 'UGD 24 jam',
        description: 'Penanganan gawat darurat tingkat pertama, observasi, dan tindakan medis atau bedah minor.',
        icon: Ambulance
    },
    {
        name: 'Persalinan dan nifas',
        description: 'Persalinan normal oleh bidan dan perawatan ibu serta bayi setelah persalinan, 24 jam.',
        icon: Baby
    },
    {
        name: 'USG kehamilan',
        description: 'Pemeriksaan USG oleh dokter terlatih beserta konsultasi dan hasil cetak.',
        icon: ScanLine
    },
    {
        name: 'Khitan modern',
        description: 'Sirkumsisi metode modern (cauter) dengan bius tanpa jarum suntik.',
        icon: Bandage
    },
    {
        name: 'Laboratorium',
        description: 'Pemeriksaan penunjang atas permintaan dokter, seperti cek darah, gula darah, dan kolesterol.',
        icon: Microscope
    },
    {
        name: 'Apotek Sehat Budi Luhur',
        description: 'Penebusan resep bagi pasien umum dan peserta JKN, dikelola apoteker.',
        icon: Pill
    },
    {
        name: 'Prolanis dan vaksinasi',
        description: 'Pengelolaan penyakit kronis (diabetes dan hipertensi) bagi peserta JKN, serta vaksinasi.',
        icon: Syringe
    }
]

export type ClinicStat = {
    value: string
    label: string
    icon: LucideIcon
}

export const stats: ClinicStat[] = [
    { value: '24 Jam', label: 'UGD & Persalinan', icon: Clock },
    { value: '16 Dokter', label: 'Poli Umum dan Poli Gigi', icon: Users },
    { value: '3 Poli', label: 'Rawat Jalan Aktif', icon: Hospital },
    { value: 'JKN', label: 'Peserta BPJS Dilayani', icon: BadgeCheck }
]

/** Senin sampai Sabtu, in schedule column order */
export const scheduleDays = [
    { short: 'Sen', long: 'Senin', weekday: 1 },
    { short: 'Sel', long: 'Selasa', weekday: 2 },
    { short: 'Rab', long: 'Rabu', weekday: 3 },
    { short: 'Kam', long: 'Kamis', weekday: 4 },
    { short: 'Jum', long: 'Jumat', weekday: 5 },
    { short: 'Sab', long: 'Sabtu', weekday: 6 }
]

export type Doctor = {
    initials: string
    name: string
    /** Senin..Sabtu, null means no practice that day */
    schedule: Array<SessionId | null>
}

export const generalDoctors: Doctor[] = [
    { initials: 'AJ', name: 'dr. Arif Jaya Saputra', schedule: ['pagi', 'pagi', 'pagi', null, null, null] },
    { initials: 'PA', name: 'dr. H. Pandith Arismunandar, MM', schedule: ['pagi', 'pagi', 'pagi', 'sore', null, null] },
    { initials: 'LW', name: 'dr. Lucky Wardani, MM', schedule: ['pagi', 'sore', null, 'pagi', 'sore', null] },
    { initials: 'GS', name: 'dr. H. Gisana Solihin Mustofa', schedule: ['pagi', 'pagi', 'sore', 'pagi', 'pagi', null] },
    { initials: 'MD', name: 'dr. Melly Dina Marcellina', schedule: ['pagi', null, 'pagi', null, 'pagi', null] },
    { initials: 'SR', name: 'dr. Sanny Rindayanti', schedule: ['sore', null, 'sore', null, 'sore', null] },
    { initials: 'EA', name: 'dr. Erwin Alvih Taufik Hidayat, MM', schedule: ['sore', null, 'sore', null, 'sore', null] },
    { initials: 'AI', name: 'dr. Atika Ichwani', schedule: ['sore', 'sore', null, null, 'sore', null] },
    { initials: 'IS', name: 'dr. Ika Setya Pratiwi', schedule: [null, 'sore', null, 'sore', null, 'sore'] }
]

const generalPractice = (session: SessionId): boolean[] => {
    return scheduleDays.map((_, index) => generalDoctors.some(doctor => doctor.schedule[index] === session))
}

/**
 * Which sessions each poli runs, Senin..Sabtu. Poli Umum follows the doctor roster above;
 * Poli Gigi and KIA/KB use demo data until their rosters are published.
 */
export const practiceDays: Record<ServiceId, Record<SessionId, boolean[]>> = {
    'poli-umum': {
        pagi: generalPractice('pagi'),
        sore: generalPractice('sore')
    },
    'poli-gigi': {
        pagi: [true, false, true, false, true, true],
        sore: [false, true, true, true, false, false]
    },
    'kia-kb': {
        pagi: [true, true, true, true, true, true],
        sore: [true, true, true, true, true, false]
    }
}

/** Demo quota per session */
export const sessionQuota: Record<ServiceId, Record<SessionId, number>> = {
    'poli-umum': { pagi: 30, sore: 24 },
    'poli-gigi': { pagi: 10, sore: 8 },
    'kia-kb': { pagi: 12, sore: 8 }
}

/** Working days offered for online booking, starting tomorrow */
export const BOOKING_DAYS = 6

/** Working days shown in the availability table */
export const AVAILABILITY_PREVIEW_DAYS = 4
